import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Form, Button, Table } from "react-bootstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FiPlus, FiEdit2, FiCopy } from "react-icons/fi";
import { postApiCall, getApiCall } from "../../../service/httpCall";
import { API_WEBHOOK_CREATE, API_WEBHOOK_REQUEST_PARAMETERS, API_WEBHOOK_UPDATE, API_WEBHOOK_GET_ONE } from "../../../service/url";
import { ROUTES } from "../../../utilities/constant";
import Loader from "../../../components/partials/components/loader";
import { toast } from "react-toastify";

const CreateManageWebhook = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = !!id;
  const previousIdRef = useRef(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingParams, setLoadingParams] = useState(true);
  const [loadingWebhook, setLoadingWebhook] = useState(isEditMode);
  
  // Webhook details state
  const [webhookData, setWebhookData] = useState({
    webhook_name: "",
    url: "",
    event: "",
    is_default: false,
  });

  // Headers state
  const [headerParam, setHeaderParam] = useState("");
  const [headerValue, setHeaderValue] = useState("");
  const [headers, setHeaders] = useState([
    { parameter: "Content-Type", value: "application/json", protected: true }
  ]);

  // Body state
  const [bodyParam, setBodyParam] = useState("");
  const [bodyValue, setBodyValue] = useState("");
  const [bodyParams, setBodyParams] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editParam, setEditParam] = useState("");
  const [editValue, setEditValue] = useState("");
  const [webhookBodyData, setWebhookBodyData] = useState(null);
  const [hasMergedBodyData, setHasMergedBodyData] = useState(false);

  // Fetch request parameters from backend
  useEffect(() => {
    setLoadingParams(true);
    getApiCall(API_WEBHOOK_REQUEST_PARAMETERS, {})
      .then((response) => {
        console.log("API Response:", response);
        // Handle the API response structure
        let params = [];
        
        // The response should be the data array directly (based on getApiCall implementation)
        // But handle both cases: direct array or wrapped in object
        let dataArray = [];
        
        if (Array.isArray(response)) {
          dataArray = response;
        } else if (response && typeof response === 'object') {
          // If response is wrapped, extract data array
          dataArray = response.data || response.parameters || response.params || response.items || [];
        }
        
        // Map the data array to our format
        params = dataArray.map(param => {
          if (typeof param === 'string') {
            return {
              parameter: param,
              value: param,
              checked: !isEditMode // Auto-check in create mode, unchecked in edit mode
            };
          } else if (typeof param === 'object' && param !== null) {
            // Handle the API structure: { id, heading, parameter_name }
            // Use parameter_name as the parameter name, and parameter_name as the value (or heading as fallback)
            const paramName = param.parameter_name || param.heading || param.parameter || param.name || param.key || '';
            // For value, use parameter_name (which represents the variable name like {campaign_id})
            const paramValue = param.parameter_name || param.heading || param.value || param.parameter || param.name || '';
            return {
              parameter: paramName,
              value: paramValue,
              checked: param.checked !== undefined ? param.checked : !isEditMode // Auto-check in create mode, unchecked in edit mode
            };
          }
          return null;
        }).filter(Boolean);
        
        console.log("Parsed params:", params);
        
        setBodyParams(params);
        
        setLoadingParams(false);
      })
      .catch((err) => {
        console.error("Error fetching request parameters:", err);
        toast.error("Failed to load request parameters");
        setLoadingParams(false);
      });
  }, []);

  // Fetch webhook data for edit mode
  useEffect(() => {
    if (isEditMode && id) {
      setLoadingWebhook(true);
      const url = API_WEBHOOK_GET_ONE.replace(":id", id);
      getApiCall(url, {})
        .then((resp) => {
          // getApiCall already returns response.data.data
          // Handle response - could be array or single object or Sequelize model
          let webhook = resp;
          if (Array.isArray(resp) && resp.length > 0) {
            webhook = resp[0];
          }
          
          console.log("Fetched webhook data:", webhook);
          
          if (webhook) {
            // Extract data from Sequelize model if needed
            const webhookDataObj = webhook.dataValues || webhook;
            
            console.log("Processing webhook data object:", webhookDataObj);
            
            // Handle mark_as_default - could be 1, 0, true, false, "1", "0", or null/undefined
            const markAsDefault = webhookDataObj.mark_as_default;
            // Convert to boolean - handle all possible formats
            const isDefault = markAsDefault === 1 || 
                             markAsDefault === true || 
                             markAsDefault === "1" || 
                             (markAsDefault !== null && markAsDefault !== undefined && String(markAsDefault) === "1");
            
            console.log("Loaded webhook - mark_as_default value:", markAsDefault, "type:", typeof markAsDefault, "converted to is_default:", isDefault);
            
            setWebhookData({
              webhook_name: webhookDataObj.webhook_name || "",
              url: webhookDataObj.url || "",
              event: webhookDataObj.event_id === 1 ? "HANGUP" : webhookDataObj.event_id === 2 ? "DTMF" : "CONNECTED_CALLS",
              is_default: isDefault,
            });

            // Parse and set headers
            try {
              const headerData = typeof webhookDataObj.header_json === "string" 
                ? JSON.parse(webhookDataObj.header_json) 
                : webhookDataObj.header_json || {};
              const headerArray = Object.entries(headerData).map(([key, value]) => ({
                parameter: key,
                value: value,
                protected: key === "Content-Type"
              }));
              setHeaders(headerArray.length > 0 ? headerArray : [
                { parameter: "Content-Type", value: "application/json", protected: true }
              ]);
            } catch (e) {
              console.error("Error parsing headers:", e);
              setHeaders([
                { parameter: "Content-Type", value: "application/json", protected: true }
              ]);
            }

            // Store webhook body data to merge with request parameters
            setWebhookBodyData(webhookDataObj.body_json);
          }
          setLoadingWebhook(false);
        })
        .catch((err) => {
          console.error("Error fetching webhook:", err);
          toast.error("Failed to load webhook data");
          setLoadingWebhook(false);
        });
    }
  }, [id, isEditMode]);

  // Reset form when switching from edit mode to create mode
  useEffect(() => {
    // Check if we're on create route (no id)
    const isInCreateMode = !id;
    const wasInEditMode = previousIdRef.current !== undefined && previousIdRef.current !== null && previousIdRef.current !== "";
    
    // If we switched from edit mode to create mode, reset all state
    if (wasInEditMode && isInCreateMode) {
      console.log("Switching from edit to create mode - resetting form state");
      // Reset all state to initial values when switching from edit to create
      setWebhookData({
        webhook_name: "",
        url: "",
        event: "",
        is_default: false,
      });
      setHeaders([
        { parameter: "Content-Type", value: "application/json", protected: true }
      ]);
      setHeaderParam("");
      setHeaderValue("");
      setBodyParam("");
      setBodyValue("");
      setEditingIndex(null);
      setEditParam("");
      setEditValue("");
      setWebhookBodyData(null);
      setHasMergedBodyData(false);
      // Reset body params to checked state (for create mode, all should be checked)
      setBodyParams(prevParams => 
        prevParams.map(param => ({ ...param, checked: true }))
      );
    }
    
    // Update the ref to track current id (only update if id actually changed)
    if (previousIdRef.current !== id) {
      previousIdRef.current = id;
    }
  }, [id, location.pathname]);

  // Merge body params when both webhook body data and params are loaded (only once)
  useEffect(() => {
    const shouldMerge = isEditMode && webhookBodyData && !hasMergedBodyData;
    if (shouldMerge && bodyParams.length > 0) {
      try {
        const bodyData = typeof webhookBodyData === "string"
          ? JSON.parse(webhookBodyData)
          : webhookBodyData || {};
        
        const hasBodyKeys = Object.keys(bodyData).length > 0;
        if (!hasBodyKeys) {
          setHasMergedBodyData(true);
          return;
        }
        
        // Mark params as checked if they exist in the webhook body
        const paramsWithChecked = bodyParams.map(param => {
          const isChecked = bodyData.hasOwnProperty(param.parameter);
          // If checked and has a value in body, use that value (remove {})
          let value = param.value;
          if (isChecked && bodyData[param.parameter]) {
            const bodyValue = String(bodyData[param.parameter]);
            value = bodyValue.replace(/[{}]/g, "") || param.parameter;
          }
          return {
            ...param,
            checked: isChecked,
            value: value
          };
        });
        
        // Also add any params that might be in body but not in the params list
        const bodyParamKeys = Object.keys(bodyData);
        bodyParamKeys.forEach(key => {
          if (!paramsWithChecked.find(p => p.parameter === key)) {
            const bodyValue = String(bodyData[key]);
            const cleanValue = bodyValue.replace(/[{}]/g, "") || key;
            paramsWithChecked.push({
              parameter: key,
              value: cleanValue,
              checked: true
            });
          }
        });
        
        setBodyParams(paramsWithChecked);
        setHasMergedBodyData(true);
      } catch (e) {
        console.error("Error merging body params:", e);
        setHasMergedBodyData(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webhookBodyData, isEditMode, hasMergedBodyData]);

  const handleWebhookChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWebhookData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Headers functions
  const handleAddHeader = () => {
    if (!headerParam.trim() || !headerValue.trim()) {
      toast.warning("Please enter both parameter and value");
      return;
    }
    
    // Check if parameter already exists
    if (headers.some(h => h.parameter.toLowerCase() === headerParam.trim().toLowerCase() && !h.protected)) {
      toast.warning("Header parameter already exists");
      return;
    }

    setHeaders([...headers, { parameter: headerParam.trim(), value: headerValue.trim(), protected: false }]);
    setHeaderParam("");
    setHeaderValue("");
  };

  const handleEditHeader = (index) => {
    const header = headers[index];
    if (header.protected) return;
    setHeaderParam(header.parameter);
    setHeaderValue(header.value);
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const handleDeleteHeader = (index) => {
    if (headers[index].protected) return;
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const copyHeadersJSON = () => {
    const headersObj = headers.reduce((acc, h) => {
      acc[h.parameter] = h.value;
      return acc;
    }, {});
    navigator.clipboard.writeText(JSON.stringify(headersObj, null, 2));
    toast.success("Headers JSON copied to clipboard");
  };

  // Body functions
  const handleAddBodyParam = () => {
    if (!bodyParam.trim() || !bodyValue.trim()) {
      toast.warning("Please enter both parameter and value");
      return;
    }

    // Check if parameter already exists
    if (bodyParams.some(p => p.parameter.toLowerCase() === bodyParam.trim().toLowerCase())) {
      toast.warning("Body parameter already exists");
      return;
    }

    setBodyParams([...bodyParams, { parameter: bodyParam.trim(), value: bodyValue.trim(), checked: true }]);
    setBodyParam("");
    setBodyValue("");
  };

  const handleToggleBodyParam = (index) => {
    const updatedParams = [...bodyParams];
    updatedParams[index].checked = !updatedParams[index].checked;
    setBodyParams(updatedParams);
  };

  const handleToggleAllBodyParams = (checked) => {
    const updatedParams = bodyParams.map(param => ({
      ...param,
      checked: checked
    }));
    setBodyParams(updatedParams);
  };

  const handleEditBodyParam = (index) => {
    const param = bodyParams[index];
    setEditingIndex(index);
    setEditParam(param.parameter);
    setEditValue(param.value);
  };

  const handleSaveEdit = () => {
    if (!editParam.trim() || !editValue.trim()) {
      toast.warning("Please enter both parameter and value");
      return;
    }

    const updatedParams = [...bodyParams];
    updatedParams[editingIndex] = {
      parameter: editParam.trim(),
      value: editValue.trim(),
      checked: updatedParams[editingIndex].checked
    };
    setBodyParams(updatedParams);
    setEditingIndex(null);
    setEditParam("");
    setEditValue("");
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditParam("");
    setEditValue("");
  };

  const copyBodyJSON = () => {
    const bodyObj = bodyParams
      .filter(p => p.checked)
      .reduce((acc, p) => {
        acc[p.parameter] = `{${p.value}}`;
        return acc;
      }, {});
    navigator.clipboard.writeText(JSON.stringify(bodyObj, null, 2));
    toast.success("Body JSON copied to clipboard");
  };

  // Generate JSON previews
  const getHeadersJSON = () => {
    const headersObj = headers.reduce((acc, h) => {
      acc[h.parameter] = h.value;
      return acc;
    }, {});
    return JSON.stringify(headersObj, null, 2);
  };

  const getBodyJSON = () => {
    const bodyObj = bodyParams
      .filter(p => p.checked)
      .reduce((acc, p) => {
        acc[p.parameter] = `{${p.value}}`;
        return acc;
      }, {});
    return JSON.stringify(bodyObj, null, 2);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!webhookData.webhook_name.trim() || !webhookData.url.trim() || !webhookData.event) {
      toast.warning("Please fill all required fields");
      return;
    }

    const urlRegex = /^(https?:\/\/)([\w\-._~:/?#[\]@!$&'()*+,;=]+)$/i;
    if (!urlRegex.test(webhookData.url.trim())) {
      toast.error("Please enter a valid Webhook URL (must start with http:// or https://)");
      return;
    }

    setLoading(true);

    // Prepare headers object (include all headers for storage)
    const allHeaders = headers.reduce((acc, h) => {
      acc[h.parameter] = h.value;
      return acc;
    }, {});

    // Prepare body object (only include checked parameters)
    // Format: { "parameter_name": "{parameter_name}" } for JSON preview
    // The value should be wrapped in curly braces to indicate it's a template variable
    const bodyObj = bodyParams
      .filter(p => p.checked && p.parameter && p.value)
      .reduce((acc, p) => {
        acc[p.parameter] = `{${p.value}}`;
        return acc;
      }, {});

    // Prepare payload according to backend schema
    const payload = {
      webhook_name: webhookData.webhook_name.trim(),
      url: webhookData.url.trim(),
      event_id: webhookData.event === "HANGUP" ? 1 : webhookData.event === "DTMF" ? 2 : 3,
      mark_as_default: webhookData.is_default ? 1 : 0, // INTEGER, not string
      header_json: JSON.stringify(allHeaders), // JSON string (includes all headers)
      body_json: JSON.stringify(bodyObj), // JSON string
    };

    const apiCall = isEditMode
      ? postApiCall(API_WEBHOOK_UPDATE.replace(":id", id), payload)
      : postApiCall(API_WEBHOOK_CREATE, payload);

    apiCall
      .then((resp) => {
        toast.success(isEditMode ? "Webhook updated successfully!" : "Webhook created successfully!");
        setTimeout(() => {
          navigate(`/${ROUTES.MANAGE_WEBHOOK.LIST}`);
        }, 2000);
      })
      .catch((err) => {
        console.log("err", err);
        toast.error(typeof err === "string" ? err : JSON.stringify(err));
        setLoading(false);
      });
  };

  if (loading || loadingParams || loadingWebhook) return <Loader />;

  return (
    <div className="py-4 px-3">
      <Row className="mx-0">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h3 className="mb-2">{isEditMode ? "EDIT WEBHOOK" : "ADD WEBHOOK"}</h3>
              <hr className="mt-2 mb-4" />
              <Form onSubmit={handleSubmit}>
                {/* Webhook Details Section */}
                <div className="mb-4">
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Webhook Name <span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="webhook_name"
                          value={webhookData.webhook_name}
                          onChange={handleWebhookChange}
                          placeholder="Webhook Name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Webhook URL<span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="url"
                          value={webhookData.url}
                          onChange={handleWebhookChange}
                          placeholder="Webhook URL"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Event<span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                        <Form.Select
                          name="event"
                          value={webhookData.event}
                          onChange={handleWebhookChange}
                          required
                        >
                          <option value="">Select Events</option>
                          <option value="HANGUP">HANGUP</option>
                          <option value="DTMF">DTMF</option>
                          <option value="CONNECTED_CALLS">CONNECTED_CALLS</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>&nbsp;</Form.Label>
                        <Form.Check
                          type="checkbox"
                          name="is_default"
                          label="Make this as default"
                          checked={webhookData.is_default}
                          onChange={handleWebhookChange}
                          className="mt-2"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Headers Section */}
                <div className="mb-4">
                  <h5 className="mb-3">Headers</h5>
                  <Row>
                    <Col md={8}>
                      <Row className="mb-3">
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Parameter"
                            value={headerParam}
                            onChange={(e) => setHeaderParam(e.target.value)}
                          />
                        </Col>
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Value"
                            value={headerValue}
                            onChange={(e) => setHeaderValue(e.target.value)}
                          />
                        </Col>
                        <Col md={2}>
                          <Button variant="primary" onClick={handleAddHeader}>
                            <FiPlus /> Add
                          </Button>
                        </Col>
                      </Row>

                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Parameter</th>
                            <th>Value</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {headers.map((header, index) => (
                            <tr key={index}>
                              <td>{header.parameter}</td>
                              <td>{header.value}</td>
                              <td>
                                {header.protected ? (
                                  <span className="text-muted">Protected</span>
                                ) : (
                                  <div className="d-flex gap-2">
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => handleEditHeader(index)}
                                      className="p-0"
                                    >
                                      <FiEdit2 className="text-warning" />
                                    </Button>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => handleDeleteHeader(index)}
                                      className="p-0"
                                    >
                                      <span className="text-danger">Delete</span>
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6>Headers JSON</h6>
                        <Button variant="link" size="sm" onClick={copyHeadersJSON} className="p-0">
                          <FiCopy />
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={8}
                        value={getHeadersJSON()}
                        readOnly
                        style={{ fontFamily: "monospace", fontSize: "12px" }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* Body Section */}
                <div className="mb-4">
                  <h5 className="mb-3">Body</h5>
                  <Row>
                    <Col md={8}>
                      <Row className="mb-3">
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Parameter"
                            value={bodyParam}
                            onChange={(e) => setBodyParam(e.target.value)}
                          />
                        </Col>
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Value"
                            value={bodyValue}
                            onChange={(e) => setBodyValue(e.target.value)}
                          />
                        </Col>
                        <Col md={2}>
                          <Button variant="primary" onClick={handleAddBodyParam}>
                            <FiPlus /> Add
                          </Button>
                        </Col>
                      </Row>

                      <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #dee2e6" }}>
                        <Table striped bordered hover style={{ marginBottom: 0 }}>
                          <thead style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                            <tr>
                              <th>
                                <Form.Check
                                  type="checkbox"
                                  checked={bodyParams.length > 0 && bodyParams.every(p => p.checked)}
                                  onChange={(e) => handleToggleAllBodyParams(e.target.checked)}
                                />
                              </th>
                              <th>Parameter</th>
                              <th>Value</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingParams ? (
                              <tr>
                                <td colSpan={4} className="text-center">
                                  <Loader />
                                </td>
                              </tr>
                            ) : bodyParams.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="text-center text-muted">
                                  No parameters available
                                </td>
                              </tr>
                            ) : (
                              bodyParams.map((param, index) => (
                                <tr key={index}>
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      checked={!!param.checked}
                                      onChange={() => handleToggleBodyParam(index)}
                                    />
                                  </td>
                                  <td>
                                    {editingIndex === index ? (
                                      <Form.Control
                                        type="text"
                                        value={editParam}
                                        onChange={(e) => setEditParam(e.target.value)}
                                        style={{ minWidth: "150px" }}
                                      />
                                    ) : (
                                      <span className={editingIndex === index ? "text-primary" : ""}>
                                        {param.parameter || ""}
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {editingIndex === index ? (
                                      <Form.Control
                                        type="text"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        style={{ minWidth: "150px" }}
                                      />
                                    ) : (
                                      <span>{param.value || ""}</span>
                                    )}
                                  </td>
                                  <td>
                                    {editingIndex === index ? (
                                      <div className="d-flex gap-2">
                                        <Button
                                          variant="success"
                                          size="sm"
                                          onClick={handleSaveEdit}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          variant="secondary"
                                          size="sm"
                                          onClick={handleCancelEdit}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    ) : (
                                      <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => handleEditBodyParam(index)}
                                        className="p-0"
                                      >
                                        <FiEdit2 className="text-warning" />
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6>Selected Body JSON</h6>
                        <Button variant="link" size="sm" onClick={copyBodyJSON} className="p-0">
                          <FiCopy />
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={15}
                        value={getBodyJSON()}
                        readOnly
                        style={{ fontFamily: "monospace", fontSize: "12px" }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* Submit Button */}
                <div className="mt-4">
                  <Button variant="primary" type="submit" size="md" disabled={loading}>
                    {isEditMode ? "Update Webhook" : "Add Webhook"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CreateManageWebhook;

